import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import { GET_ORGANIZATION_BILLING_ACCOUNTS } from "graphql/queries";
import { UPDATE_ORGANIZATION_BILLING_ACCOUNT } from "graphql/mutations";
import { useToast } from "hooks";

/**
 * useBillingAccounts
 * Custom hook to fetch billing accounts for an organization and handle updates
 * @param {string} organizationId - The organization ID to fetch billing accounts for
 * @returns {Object} - { billingAccounts, loading, updating, error, handleBillingAccountChange }
 */
export default function useBillingAccounts({ organizationId } = {}) {
  const { toast } = useToast();
  const { data, loading, error } = useQuery(GET_ORGANIZATION_BILLING_ACCOUNTS, {
    variables: {
      filters: { organizationId },
    },
    skip: !organizationId,
  });

  // Update billing account mutation
  const [updateBillingAccount, { loading: updating }] = useMutation(UPDATE_ORGANIZATION_BILLING_ACCOUNT);

  const billingAccounts = data?.organizationBillingAccounts?.nodes || [];

  /**
   * Handle billing account change
   * @param {string} accountId - The billing account ID to set
   * @param {Object} termSheet - The current termsheet object
   * @param {Function} onSuccess - Optional callback called with accountId on success
   */
  const handleBillingAccountChange = (accountId, termSheet) =>
    updateBillingAccount({
      variables: {
        input: {
          versionId: termSheet?.versionId,
          billingAccountId: accountId,
        },
      },
      onCompleted: () =>
        toast.success(<FormattedMessage id="TermSheets.LegacyTermSheetTable.UpdateBillingAccount.Success" />),
      onError: () => toast.error(<FormattedMessage id="TermSheets.LegacyTermSheetTable.UpdateBillingAccount.Error" />),
    });

  return {
    billingAccounts,
    loading,
    updating,
    error,
    handleBillingAccountChange,
  };
}
